import type { ClientMessage, ServerMessage, SessionState } from '../../shared/types';
import { onAppStateChange }                               from './cap-bridge';

// ─── Config ───────────────────────────────────────────────────────────────────

// When running inside Capacitor the page origin is a native bridge URL,
// not the real server. Always use production in that case.
const _isCapacitor = !!(window as unknown as { Capacitor?: unknown }).Capacitor;
const _isDev       = !_isCapacitor && location.hostname === 'localhost';

const WS_BASE  = _isDev ? 'ws://localhost:8787'  : 'wss://lava.ger.soy';
const API_BASE = _isDev ? 'http://localhost:8787' : 'https://lava.ger.soy';

const RECONNECT_BASE_MS  = 1_000;
const RECONNECT_MAX_MS   = 30_000;
const RECONNECT_ATTEMPTS = 10;
const PING_INTERVAL_MS   = 10_000;

// ─── Types ────────────────────────────────────────────────────────────────────

type MessageHandler = (msg: ServerMessage) => void;
type StatusHandler  = (status: SocketStatus) => void;
export type SocketStatus = 'connecting' | 'connected' | 'reconnecting' | 'disconnected';

// ─── Socket ───────────────────────────────────────────────────────────────────

class LavaSocket {
  private ws:               WebSocket | null = null;
  private roomCode:         string | null = null;
  private playerId:         string | null = null;
  private username:         string | null = null;
  private reconnectAttempt: number = 0;
  private reconnectTimer:   ReturnType<typeof setTimeout> | null = null;
  private pingTimer:        ReturnType<typeof setInterval> | null = null;
  private intentionalClose: boolean = false;
  private suspended:        boolean = false;
  private messageHandlers:  Set<MessageHandler> = new Set();
  private statusHandlers:   Set<StatusHandler> = new Set();

  constructor() {
    // iOS/Android: close WebSocket when app backgrounds, reconnect on resume.
    // This prevents the server from hanging on a stale connection after iOS
    // suspends the process and tears down TCP without a proper close frame.
    onAppStateChange((active) => {
      if (active) {
        this.suspended = false;
        if (this.roomCode) this.connect(); // reconnect
      } else {
        this.suspended = true;
        this.intentionalClose = true;
        this.cleanup();
        this.intentionalClose = false; // reset so reconnect logic stays armed
      }
    });
  }

  // ─── Public API ─────────────────────────────────────────────────────────────

  async createRoom(username: string): Promise<string> {
    this.username = username;
    this.playerId = this.getOrCreatePlayerId();

    const res = await fetch(`${API_BASE}/sessions/create`, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({ playerId: this.playerId, username }),
    });

    if (!res.ok) throw new Error('Failed to create room');

    const { roomCode } = await res.json() as { roomCode: string };
    this.roomCode = roomCode;
    this.connect();
    return roomCode;
  }

  async joinRoom(roomCode: string, username: string): Promise<void> {
    this.username = username;
    this.playerId = this.getOrCreatePlayerId();
    this.roomCode = roomCode.toUpperCase();

    const res = await fetch(`${API_BASE}/sessions/${this.roomCode}/join`, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({ playerId: this.playerId, username }),
    });

    if (!res.ok) {
      const { error } = await res.json() as { error: string };
      throw new Error(error ?? 'Room not found');
    }

    this.connect();
  }

  send(msg: ClientMessage): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(msg));
    }
  }

  disconnect(): void {
    this.intentionalClose = true;
    this.cleanup();
  }

  onMessage(handler: MessageHandler): () => void {
    this.messageHandlers.add(handler);
    return () => this.messageHandlers.delete(handler);
  }

  onStatus(handler: StatusHandler): () => void {
    this.statusHandlers.add(handler);
    return () => this.statusHandlers.delete(handler);
  }

  // ─── Connection ─────────────────────────────────────────────────────────────

  private connect(): void {
    if (!this.roomCode || !this.playerId || !this.username) return;

    this.intentionalClose = false;
    this.setStatus('connecting');

    const url = `${WS_BASE}/sessions/${this.roomCode}/connect`
      + `?playerId=${encodeURIComponent(this.playerId)}`
      + `&username=${encodeURIComponent(this.username)}`;

    this.ws = new WebSocket(url);

    this.ws.onopen = () => {
      this.reconnectAttempt = 0;
      this.setStatus('connected');
      this.startPing();
    };

    this.ws.onmessage = (event) => {
      const msg = JSON.parse(event.data as string) as ServerMessage;
      if (msg.type === 'pong') return; // heartbeat, ignore
      this.messageHandlers.forEach(h => h(msg));
    };

    this.ws.onclose = () => {
      this.stopPing();
      if (!this.intentionalClose) this.scheduleReconnect();
      else this.setStatus('disconnected');
    };

    this.ws.onerror = () => {
      this.ws?.close();
    };
  }

  private scheduleReconnect(): void {
    if (this.suspended) return; // backgrounded — wait for resume
    if (this.reconnectAttempt >= RECONNECT_ATTEMPTS) {
      this.setStatus('disconnected');
      return;
    }

    this.setStatus('reconnecting');

    const delay = Math.min(
      RECONNECT_BASE_MS * Math.pow(2, this.reconnectAttempt),
      RECONNECT_MAX_MS
    );

    this.reconnectTimer = setTimeout(() => {
      this.reconnectAttempt++;
      this.connect();
    }, delay);
  }

  private cleanup(): void {
    this.stopPing();
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.ws?.close();
    this.ws = null;
  }

  // ─── Ping ───────────────────────────────────────────────────────────────────

  private startPing(): void {
    this.stopPing();
    this.pingTimer = setInterval(() => {
      this.send({ type: 'ping' });
    }, PING_INTERVAL_MS);
  }

  private stopPing(): void {
    if (this.pingTimer) clearInterval(this.pingTimer);
    this.pingTimer = null;
  }

  // ─── Status ─────────────────────────────────────────────────────────────────

  private setStatus(status: SocketStatus): void {
    this.statusHandlers.forEach(h => h(status));
  }

  // ─── Player ID ──────────────────────────────────────────────────────────────

  private getOrCreatePlayerId(): string {
    let id = localStorage.getItem('lava_player_id');
    if (!id) {
      id = crypto.randomUUID();
      localStorage.setItem('lava_player_id', id);
    }
    return id;
  }
}

// ─── Singleton ────────────────────────────────────────────────────────────────

export const socket = new LavaSocket();

// ─── REST helpers ─────────────────────────────────────────────────────────────

export async function apiPost<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    method:  'POST',
    headers: { 'Content-Type': 'application/json' },
    body:    JSON.stringify(body),
  });
  if (!res.ok) {
    const { error } = await res.json() as { error: string };
    throw new Error(error ?? `Request failed: ${res.status}`);
  }
  return res.json() as Promise<T>;
}
